"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import api, { App } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { AddAppDialog } from "@/components/apps/add-app-dialog";
import { AppAvatar } from "@/components/apps/app-avatar";
import { formatTimeAgo } from "@/lib/format";
import {
  Plus,
  Loader2,
  AlertCircle,
  Smartphone,
  Settings,
  ArrowRight,
} from "lucide-react";
import { PlatformIcon } from "@/components/apps/platform-icon";

export default function AppsPage() {
  const { currentTeam, user } = useAuth();
  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchApps = useCallback(async () => {
    if (!currentTeam) return;
    setLoading(true);
    try {
      const result = await api.getApps(currentTeam.id);
      setApps(result || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to load applications");
    } finally {
      setLoading(false);
    }
  }, [currentTeam]);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  if (loading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
        <AlertCircle className="size-10 text-destructive" />
        <p className="text-muted-foreground">{error}</p>
        <Button variant="outline" onClick={fetchApps}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Applications</h1>
          <p className="text-sm text-muted-foreground">
            Overview of your apps
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="size-4" />
          Add application
        </Button>
      </div>

      {/* Personal Account section */}
      <div className="space-y-4">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          {user?.name ? `${user.name}'s Account` : "Personal Account"}
        </h2>

        {apps.length === 0 ? (
          <div className="rounded-lg border border-dashed p-12">
            <div className="flex flex-col items-center justify-center gap-4">
              <div className="flex items-center justify-center rounded-xl bg-muted p-4">
                <Smartphone className="size-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold">No applications yet</h3>
              <p className="text-sm text-muted-foreground">
                Add your first app to get started.
              </p>
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="size-4" />
                Add application
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {apps.map((app) => (
              <div
                key={app.id}
                className="flex items-center gap-4 rounded-lg border bg-card px-4 py-3 transition-colors hover:bg-muted/30"
              >
                <div className="relative">
                  <AppAvatar name={app.name} size="md" />
                  <PlatformIcon projectType={app.project_type} className="absolute -bottom-1 -right-1" />
                </div>

                <div className="flex-1 min-w-0">
                  <Link
                    href={`/apps/${app.id}/builds`}
                    className="text-sm font-semibold hover:underline"
                  >
                    {app.name}
                  </Link>
                  <p className="text-xs text-muted-foreground">
                    {app.last_build_at
                      ? `Last build: ${formatTimeAgo(app.last_build_at)}`
                      : "No builds yet"}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <Link href={`/apps/${app.id}/settings`}>
                    <Button variant="ghost" size="icon" className="text-muted-foreground">
                      <Settings className="size-4" />
                    </Button>
                  </Link>
                  <Link href={`/apps/${app.id}/builds`}>
                    <Button variant="outline" size="sm" className="gap-1">
                      Start new build
                      <ArrowRight className="size-3.5" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AddAppDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={fetchApps}
      />
    </div>
  );
}
